import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AISM_STANDALONE } from "../components/Aism/standalone";
import SnackbarComponent from "../components/Snackbar/Index.snackbar";
import LoginModal from "../components/Modals/LoginModal/Index.loginModal";
import { snackbarObj } from "../redux/common/common.selectors";
import { loginModal } from "../redux/auth/auth.selectors";
import { useAppSelector } from "../redux/store/store";
import { FullAppSkeleton } from "../components/Loader/FullAppSkeleton";

// Import all paths
import * as Paths from "./Paths";

// ProjectedRoute to validate if the session is valid or user has relevant permission
import ProtectedRoute from "./ProtectedRoutes";

// Import Page Components
const NotFound = lazy(() => import("../pages/Authentication/NotFound/Index.notFound"));
const Layout = lazy(() => import("./Layout"));
const Login = lazy(() => import("../pages/Authentication/Login/Index.login"));
const ForgotPassword = lazy(() => import("../pages/Authentication/ForgotPassword/Index.forgotPassword"));

// Back Office
// Users
const Users = lazy(() => import("../pages/backOffice/users/Index.users"));
const CreateUser = lazy(() => import("../pages/backOffice/users/Users/CreateUser/Index.create"));
const UpdateUser = lazy(() => import("../pages/backOffice/users/Users/UpdateUser/Index.update"));
const CreateRole = lazy(() => import("../pages/backOffice/users/Roles/CreateRole/Index.create"));
const EditRole = lazy(() => import("../pages/backOffice/users/Roles/EditRole/Index.editRole"));
const Subcontractors = lazy(() => import("../pages/backOffice/subcontractors/subcontractorsListing/Index.subcontractorListing"));
const SubcontractorDetails = lazy(() => import("../pages/backOffice/subcontractors/subcontractorsDetails/Index.subcontractorsDetails"));

// Projects
const ProjectsListing = lazy(() => import("../pages/backOffice/projects/projectsListing/Index.projectsListing"));
const CreateProject = lazy(() => import("../pages/backOffice/projects/createProject/Index.createProject"));
// Offices
const OfficesListing = lazy(() => import("../pages/backOffice/offices/Index.offices"));
const CreateOffice = lazy(() => import("../pages/backOffice/offices/Offices/CreateOffice/Index.createOffice"));
const UpdateOffice = lazy(() => import("../pages/backOffice/offices/Offices/UpdateOffice/Index.updateOffice"));

// Overview Template
const OverviewTemplate = lazy(() => import("../pages/backOffice/overviewTemplate/Index.overviewTemplate"));
// Notifications
const Notifications = lazy(() => import('../pages/backOffice/notifications/Index.notifications'));

// Folders
const Folders = lazy(() => import("../pages/backOffice/folders/Index.templateFolders"));

// Sales
// Projects
const SalesProjects = lazy(() => import('../pages/sales/projects/projectsListing/Index.projectListing'));
const SalesProjectDetails: any = lazy(() => import('../pages/sales/projects/projectDetails/Index.projectDetails'));
// Proposals
const ProposalsListing = lazy(() => import('../pages/sales/proposals/proposalsListing/Index.proposalsListing'));
const SalesProposalDetails = lazy(() => import('../pages/sales/proposals/proposalDetails/Index.proposalDetails'))
// AI Estimates (AISM — feature-flagged)
const EstimatesListing = lazy(() => import('../pages/sales/estimates/estimatesListing/Index.estimatesListing'));
const EstimateDetails = lazy(() => import('../pages/sales/estimates/estimateDetails/Index.estimateDetails'));
// Pipeline (AISM Deal Module — feature-flagged)
const PipelineDashboard = lazy(() => import('../pages/sales/pipeline/pipelineDashboard/Index.pipelineDashboard'));
const PipelineDealDetails = lazy(() => import('../pages/sales/pipeline/dealDetails/Index.dealDetails'));
// Walkthroughs (AISM Walkthrough Readiness — feature-flagged)
const WalkthroughsQueue = lazy(() => import('../pages/sales/walkthroughs/walkthroughsQueue/Index.walkthroughsQueue'));
const ReadinessDoc = lazy(() => import('../pages/sales/walkthroughs/readinessDoc/Index.readinessDoc'));
const MoodboardDoc = lazy(() => import('../pages/sales/walkthroughs/moodboardDoc/Index.moodboardDoc'));

// Project Management
const PMDashboard = lazy(() => import('../pages/projectManagement/dashboard/Index.dashboard'));
const PMProjectDetails: any = lazy(() => import('../pages/projectManagement/projects/projectDetails/Index.projectDetails'));
const PMProposalDetails = lazy(() => import('../pages/projectManagement/proposals/proposalDetails/Index.proposalDetails'));

// Design Management
const DMDashboard = lazy(() => import('../pages/designManagement/dashboard/Index.dashboard'));
const DMProjectDetails: any = lazy(() => import('../pages/designManagement/projects/projectDetails/Index.projectDetails'));
const DMProposalDetails = lazy(() => import('../pages/designManagement/proposals/proposalDetails/Index.proposalDetails'));


// Finance Management
const FMDashboard = lazy(() => import('../pages/financeManagement/dashboard/Index.dashboard'));
const Invoices = lazy(() => import('../pages/financeManagement/dashboard/Invoices/Index.invoices'));
const BudgetVActualSpend = lazy(() => import('../pages/financeManagement/dashboard/BudgetVActualSpend/Index.budgetVActualSpend'));
const ProfitMargins = lazy(() => import('../pages/financeManagement/dashboard/ProfitMargins/Index.profitMargins'));

const CashFlow = lazy(() => import('../pages/financeManagement/dashboard/CashFlow/Index.cashFlow'));
const ProjectCashFlow = lazy(() => import('../pages/financeManagement/dashboard/CashFlow/ProjectCashFlow/Index.projectCashFlow'));

// Cash Flow - commented out for now; uncomment to revert
// const FMCashFlow = lazy(() => import('../pages/financeManagement/cashFlow/Index.cashFlow'));
const FMAccountPayable = lazy(() => import('../pages/financeManagement/accountPayable/Index.accountPayable'));


const SubcontractorsPayments = lazy(() => import('../pages/financeManagement/dashboard/SubcontractorsPayments/Index.subcontractorsPayments'));
const Expenses = lazy(() => import('../pages/financeManagement/dashboard/Expenses/Index.Expenses'));
const VendorPayments = lazy(() => import('../pages/financeManagement/dashboard/VendorPayments/Index.vendorPayments'));

const FMProjectListing: any = lazy(() => import('../pages/financeManagement/projects/projectsListing/Index.projectListing'));
const FMProjectDetails: any = lazy(() => import('../pages/financeManagement/projects/projectDetails/Index.projectDetails'));

const FMProposalDetails = lazy(() => import('../pages/financeManagement/proposals/proposalDetails/Index.proposalDetails'));


// Design Management
const DesignDashboard = lazy(() => import('../pages/designManagement/dashboard/Index.dashboard'));
const DesignProjectDetails: any = lazy(() => import('../pages/sales/projects/projectDetails/Index.projectDetails'));

const StandaloneLayout = lazy(() => import("./StandaloneLayout"));

// Redirection path
const authenticationPath = "/unauthorized";

/** Standalone-estimator guard: any valid Hub login may use the estimator, so
 *  only the session is checked — the Hub's per-module permission payload is
 *  irrelevant here (the estimator users aren't Sales-module users). Reading
 *  localStorage at render time keeps the post-login navigate() working. */
const RequireLogin = ({ children }: { children: JSX.Element }) =>
  localStorage.getItem("token") ? children : <Navigate to={Paths.LOGIN} replace />;

function RoutesWapper() {
  const isAuthenticated = !!localStorage.getItem("token");

  const snackObj = useAppSelector(snackbarObj);
  const loginMod = useAppSelector(loginModal);

  // Estimator-only build: login + AI Estimates, nothing else exists.
  if (AISM_STANDALONE) {
    return (
      <>
        <SnackbarComponent snackObj={snackObj} />
        <BrowserRouter>
          <Suspense fallback={<FullAppSkeleton isLoading={true} />}>
            <LoginModal modalObj={loginMod} />
            <Routes>
              <Route element={<StandaloneLayout />}>
                <Route
                  path={Paths.SALES_ESTIMATES}
                  element={<RequireLogin><EstimatesListing /></RequireLogin>}
                />
                <Route
                  path={Paths.SALES_ESTIMATE_DETAILS}
                  element={<RequireLogin><EstimateDetails /></RequireLogin>}
                />
              </Route>
              <Route path={Paths.LOGIN} element={<Login />} />
              <Route path={Paths.FORGOT_PASSWORD} element={<ForgotPassword />} />
              {/* everything else lands on the listing (or login via its guard) */}
              <Route path="*" element={<Navigate to={Paths.SALES_ESTIMATES} replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </>
    );
  }

  return (
    <>
      {/* snackbar component */}
      <SnackbarComponent snackObj={snackObj} />
      {/* snackbar component */}

      <BrowserRouter>
        <Suspense fallback={<FullAppSkeleton isLoading={true} />}>
        {/* <FullAppSkeleton isLoading={true} /> */}

          {/* login modal */}
          <LoginModal
            modalObj={loginMod}
          />
          {/* login modal */}

          <Routes>
            {/* following routes if you need to render inside the layout (Dashboard,Documents) */}
            <Route element={
              <Layout
                documentType={""}
                view={""}
                handleView={""}
              />
            }>
              {/* Projects */}
              <Route
                index
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    module_name="projects"
                    permissions={["view"]}
                    outlet={<ProjectsListing />}
                  />
                }
              />
              <Route
                path={Paths.CREATE_PROJECT}
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    module_name="projects"
                    permissions={["create", "edit"]}
                    outlet={<CreateProject />}
                  />
                }
              />

              {/* Sales */}
              <Route
                path={Paths.SALES_PROJECTS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["view"]} outlet={<SalesProjects />} />}
              />
              <Route
                path={Paths.SALES_PROJECT_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["create", "edit"]} outlet={<SalesProjectDetails parent={"sales"} />} />}
              />
              <Route
                path={Paths.SALES_PROPOSALS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["view"]} outlet={<ProposalsListing />} />}
              />
              <Route
                path={Paths.SALES_PROPOSAL_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["create", "edit"]} outlet={<SalesProposalDetails />} />}
              />
              {process.env.REACT_APP_AISM_ESTIMATES_ENABLED === "true" && (
                <>
                  <Route
                    path={Paths.SALES_ESTIMATES}
                    element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["view"]} outlet={<EstimatesListing />} />}
                  />
                  <Route
                    path={Paths.SALES_ESTIMATE_DETAILS}
                    element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["view"]} outlet={<EstimateDetails />} />}
                  />
                </>
              )}
              {process.env.REACT_APP_AISM_DEALS_ENABLED === "true" && (
                <>
                  <Route
                    path={Paths.SALES_PIPELINE}
                    element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["view"]} outlet={<PipelineDashboard />} />}
                  />
                  <Route
                    path={Paths.SALES_PIPELINE_DEAL_DETAILS}
                    element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["view"]} outlet={<PipelineDealDetails />} />}
                  />
                </>
              )}
              {process.env.REACT_APP_AISM_WALKTHROUGHS_ENABLED === "true" && (
                <>
                  <Route
                    path={Paths.SALES_WALKTHROUGHS}
                    element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["view"]} outlet={<WalkthroughsQueue />} />}
                  />
                  <Route
                    path={Paths.SALES_WALKTHROUGH_READINESS}
                    element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["view"]} outlet={<ReadinessDoc />} />}
                  />
                  <Route
                    path={Paths.SALES_WALKTHROUGH_MOODBOARD}
                    element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="sales" permissions={["view"]} outlet={<MoodboardDoc />} />}
                  />
                </>
              )}

              {/* Project Management */}
              <Route
                path={Paths.PM_DASHBOARD}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<PMDashboard />} />}
              />
              <Route
                path={Paths.PM_PROJECT_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<PMProjectDetails parent={"pm"} />} />}
              />
              <Route
                path={Paths.PM_PROPOSAL_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<PMProposalDetails />} />}
              />

              {/* Design Management */}
              <Route
                path={Paths.DM_DASHBOARD}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="designer" permissions={["view"]} outlet={<DMDashboard />} />}
              />
              <Route
                path={Paths.DM_PROJECT_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="designer" permissions={["view"]} outlet={<DMProjectDetails parent={"pm"} />} />}
              />
              <Route
                path={Paths.DM_PROPOSAL_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<DMProposalDetails />} />}
              />

              {/* Finance Management */}
              <Route
                path={Paths.FM_DASHBOARD_ALL}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<FMDashboard />} />}
              />
              <Route
                path={Paths.FM_DASHBOARD_INVOICES}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<Invoices />} />}
              />
              <Route
                path={Paths.FM_DASHBOARD_CASH_FLOW}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<CashFlow />} />}
              />
              <Route
                path={Paths.FM_DASHBOARD_PROJECT_CASH_FLOW}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<ProjectCashFlow />} />}
              />
              <Route
                path={Paths.FM_DASHBOARD_PROFIT_MARGINS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<ProfitMargins />} />}
              />
              <Route
                path={Paths.FM_DASHBOARD_BUDGET_ACTUAL_SPEND}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<BudgetVActualSpend />} />}
              />
              {/* <Route
                path={Paths.FM_DASHBOARD_SUBCONTRACTORS_PAYMENTS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<SubcontractorsPayments />} />}
              /> */}
              <Route
                path={Paths.FM_DASHBOARD_EXPENSES}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<Expenses />} />}
              />
              <Route
                path={Paths.FM_DASHBOARD_VENDORS_PAYMENTS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<VendorPayments />} />}
              />
              <Route
                path={Paths.FM_DASHBOARD_SUBCONTRACTOR_PAYMENTS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<SubcontractorsPayments />} />}
              />

              {/* Cash Flow - commented out for now; uncomment to revert */}
              {/* <Route
                path={Paths.FM_CASH_FLOW}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<FMCashFlow />} />}
              /> */}
              <Route
                path={Paths.FM_ACCOUNT_PAYABLE}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<FMAccountPayable />} />}
              />

              <Route
                path={Paths.FM_PROJECTS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<FMProjectListing parent={"pm"} />} />}
              />
              <Route
                path={Paths.FM_PROJECT_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<FMProjectDetails parent={"pm"} />} />}
              />
              <Route
                path={Paths.FM_PROPOSAL_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<FMProposalDetails />} />}
              />

              {/* Design Management */}
              <Route
                path={Paths.DESIGN_DASHBOARD}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="designer" permissions={["view"]} outlet={<DesignDashboard />} />}
              />
              <Route
                path={Paths.DESIGN_PROJECT_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="designer" permissions={["view"]} outlet={<DesignProjectDetails parent={"pm"} />} />}
              />

              {/* Users */}
              <Route
                path={Paths.USERS_LISTING}
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    module_name="users"
                    permissions={["create", "edit", "delete", "view"]}
                    outlet={<Users />}
                  />
                }
              />
              <Route
                path={Paths.CREATE_USER}
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    module_name="users"
                    permissions={["create", "edit"]}
                    outlet={<CreateUser />}
                  />
                }
              />

              <Route
                path={Paths.UPDATE_USER}
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    module_name="users"
                    permissions={["create", "edit"]}
                    outlet={<UpdateUser />}
                  />
                }
              />

              <Route
                path={Paths.CREATE_ROLE}
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    module_name="users"
                    permissions={["create", "edit"]}
                    outlet={<CreateRole />}
                  />
                }
              />
              <Route
                path={Paths.EDIT_ROLE}
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    module_name="users"
                    permissions={["create", "edit"]}
                    outlet={<EditRole />}
                  />
                }
              />

              {/* Overview Template */}
              <Route
                path={Paths.OVERVIEW_TEMPLATE}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<OverviewTemplate />} />}
              />

              {/* Notifications */}
              <Route
                path={Paths.NOTIFICATIONS_LISTING}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<Notifications />} />}
              />

              {/* Folders */}
              <Route
                path={Paths.FOLDERS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<Folders />} />}
              />

              {/* Subcontractors */}
              <Route
                path={Paths.SUBCONTRACTOR_LISTING}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<Subcontractors />} />}
              />
              {/* Subcontractor Details */}
              
              <Route
                path={Paths.SUBCONTRACTOR_DETAILS}
                element={<ProtectedRoute authenticationPath={authenticationPath} isAuthenticated={isAuthenticated} module_name="pm" permissions={["view"]} outlet={<SubcontractorDetails />} />}
              />

              {/* Offices */}
              <Route
                path={Paths.OFFICES_LISTING}
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    // "offices" needs to be added into the user-permissions object from backend.
                    // temporarily usng "users" to gain access for development
                    module_name="users"
                    permissions={["create", "edit", "delete", "view"]}
                    outlet={<OfficesListing />}
                  />
                }
              />
              <Route
                path={Paths.CREATE_OFFICE}
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    // "offices" needs to be added into the user-permissions object from backend.
                    // temporarily usng "users" to gain access for development
                    module_name="users"
                    permissions={["create", "edit"]}
                    outlet={<CreateOffice />}
                  />
                }
              />
              <Route
                path={Paths.UPDATE_OFFICE}
                element={
                  <ProtectedRoute
                    authenticationPath={authenticationPath}
                    isAuthenticated={isAuthenticated}
                    // "offices" needs to be added into the user-permissions object from backend.
                    // temporarily usng "users" to gain access for development
                    module_name="users"
                    permissions={["create", "edit"]}
                    outlet={<UpdateOffice />}
                  />
                }
              />
            </Route>
            {/* following routes do not need any layout (header,footer) */}
            <Route path={Paths.LOGIN} element={<Login />} />
            <Route path={Paths.FORGOT_PASSWORD} element={<ForgotPassword />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

export default RoutesWapper;
